/**
 * Expiry display for the trash view (DV-2524). Enforcement is entirely
 * server-side (asset-service's hourly `TrashRetentionJob`); nothing here
 * schedules, polls, or purges.
 *
 * This module renders the DEADLINE, not a countdown. `trashedAt + retention` is
 * an exact instant, but the moment of deletion is not: the sweep ticks hourly,
 * takes 500 assets at a time, and defers any asset whose Legacy DELETE mirror is
 * still undelivered — all of which move deletion later, never earlier. A day
 * count would therefore be a lossy rendering of a precise number, and every
 * rounding rule picks a direction to be wrong in (ceiling tells someone with two
 * hours left they have a day; flooring calls a rolling 24-hour window "Today").
 * So the label names the date the item becomes eligible for permanent deletion,
 * and urgency is carried by the ≤7-day warning tone instead.
 */
import dayjs from 'dayjs';
import type { Asset } from '../../../types/asset.types';

export const TRASH_RETENTION_DAYS = 30;
export const EXPIRING_SOON_DAYS = 7;

const MS_PER_DAY = 86_400_000;
// Epoch SECONDS past this are implausible (year 5138), so a larger number must
// already be milliseconds. Lets a future backend switch to millis pass through
// unharmed instead of rendering a date 1000x in the future.
const MILLIS_THRESHOLD = 1e11;

export type TrashTimestamps = Pick<Asset, 'trashedAt' | 'trashExpiresAt'>;

/**
 * Normalizes the two shapes a trash timestamp can arrive in. `trashedAt` carries
 * no `@JsonFormat` on the backend entity (unlike every other Instant on
 * `Assets`), so it serializes as raw epoch seconds with a nanos fraction; once
 * the annotation ships it becomes ISO-8601. Accepts either.
 *
 * CLEANUP: when the backend fix ships, the numeric branch is dead — delete it.
 */
export function parseTrashTimestamp(
  value: string | number | null | undefined
): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    return value > MILLIS_THRESHOLD ? value : Math.round(value * 1000);
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * The instant this item becomes eligible for permanent deletion, or null when
 * neither timestamp is usable.
 *
 * CLEANUP: once the backend serializes `trashExpiresAt`, the derived branch is
 * dead — the server owns the retention window and this becomes a straight read.
 */
export function trashExpiresAtMs(asset: TrashTimestamps): number | null {
  const serverExpiry = parseTrashTimestamp(asset.trashExpiresAt);
  if (serverExpiry !== null) return serverExpiry;
  const trashedAt = parseTrashTimestamp(asset.trashedAt);
  if (trashedAt === null) return null;
  return trashedAt + TRASH_RETENTION_DAYS * MS_PER_DAY;
}

/** Pill copy: the deadline date, past tense once it has passed. */
export function expiryLabel(
  expiresAtMs: number,
  now: number = Date.now()
): string {
  const date = dayjs(expiresAtMs).format('MMM D');
  return expiresAtMs <= now ? `Expired ${date}` : `Expires ${date}`;
}

/** Warning tone inside the final week — and for anything already overdue. */
export function isExpiringSoon(
  expiresAtMs: number,
  now: number = Date.now()
): boolean {
  return expiresAtMs - now <= EXPIRING_SOON_DAYS * MS_PER_DAY;
}
